<template>
  <div class="bitts-filter-select self-stretch">
    <BittsDropdownMenu
      v-model="model"
      v-model:is-open="isDropdownOpen"
      :options="options"
      mode="single"
      :use-search="useSearch"
      variant="default"
      :search-placeholder="searchPlaceholder"
      :show-checkbox="false"
      :disabled="disabled"
    >
      <template #default="{ isOpen }">
        <div
          role="combobox"
          :aria-expanded="isOpen"
          aria-haspopup="listbox"
          :aria-disabled="disabled"
          :aria-label="ariaLabel"
          :tabindex="disabled ? -1 : 0"
          class="bitts-filter-select__trigger"
          :class="{
            disabled,
          }"
        >
          <div v-if="!selectedItem" class="bitts-filter-select__placeholder">
            {{ placeholder }}
          </div>

          <div v-else class="bitts-filter-select__selected-item">
            <slot name="selected-item" :item="selectedItem">
              <slot name="prefix" :option="selectedItem" />
              <span
                class="bitts-filter-select__label"
                :title="selectedItem.label"
              >
                {{ selectedItem.label }}
              </span>
            </slot>
          </div>

          <div class="bitts-filter-select__suffix">
            <FontAwesomeIcon
              :icon="['fak', 'chevron-down']"
              :class="{ 'rotate-180': isOpen && !disabled }"
              :style="{ width: '16px', height: '16px' }"
              class="transition-transform"
            />
          </div>
        </div>
      </template>

      <template v-if="$slots.option" #option="{ option }">
        <slot name="option" :option="option" />
      </template>
      <template v-if="$slots.prefix" #prefix="{ option }">
        <slot name="prefix" :option="option" />
      </template>
      <template v-if="$slots.suffix" #suffix="{ option }">
        <slot name="suffix" :option="option" />
      </template>
    </BittsDropdownMenu>
  </div>
</template>

<script setup lang="ts" generic="TOption extends { label: string; value: any }">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';

import BittsDropdownMenu from '../BittsDropdownMenu/BittsDropdownMenu.vue';
import type { MenuGroup } from '../BittsDropdownMenu/types';
import { useGroupedMenuItems } from '../composables/useGroupedMenuItems';

const props = withDefaults(
  defineProps<{
    options: readonly (TOption | MenuGroup<TOption>)[];
    placeholder?: string;
    disabled?: boolean;
    useSearch?: boolean;
    searchPlaceholder?: string;
  }>(),
  {
    placeholder: 'Select an option',
    disabled: false,
    useSearch: true,
    searchPlaceholder: 'Search',
  },
);

defineEmits<{
  change: [value: TOption['value']];
}>();

defineSlots<{
  'selected-item': (props: { item: TOption }) => unknown;
  option: (props: { option: TOption }) => unknown;
  prefix: (props: { option: TOption }) => unknown;
  suffix: (props: { option: TOption }) => unknown;
}>();

const model = defineModel<TOption['value']>();
const isDropdownOpen = defineModel<boolean>('isOpen', { default: false });

const { flattenMenuLeaves } = useGroupedMenuItems();

const flatOptions = computed(() => flattenMenuLeaves<TOption>(props.options));

const selectedItem = computed(() =>
  flatOptions.value.find((opt) => opt.value === model.value),
);

const ariaLabel = computed(() => {
  if (!selectedItem.value) return props.placeholder;
  return `${props.placeholder}, ${selectedItem.value.label} selected`;
});
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";

.bitts-filter-select__trigger {
  @apply relative flex items-center gap-8 px-8 py-4 border border-neutral-border-strong rounded-lg
  bg-white cursor-pointer transition-colors shadow-component min-h-32 h-full;
}

.bitts-filter-select__trigger.disabled {
  @apply opacity-60 pointer-events-none;
}

.bitts-filter-select__placeholder {
  @apply flex-1 text-base text-neutral-text-placeholder;
}

.bitts-filter-select__selected-item {
  @apply flex-1 flex items-center gap-4 overflow-hidden min-w-0;
}

.bitts-filter-select__label {
  @apply text-base text-neutral-text-strong truncate;
}

.bitts-filter-select__suffix {
  @apply flex items-center text-neutral-accent;
}
</style>
